import {CommonModule} from "@angular/common";
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatBadgeModule} from "@angular/material/badge";
import {MatButtonModule} from "@angular/material/button";
import {MatChipsModule} from "@angular/material/chips";
import {MatOption} from "@angular/material/core";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatSelect} from "@angular/material/select";
import {MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel} from "@angular/material/tabs";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {
  SharedSpaceSelectionListComponent
} from "@features/leasing/lease-bookings/manage-sublease-booking/shared-space-selection-list/shared-space-selection-list.component";
import {LeaseViewComponent} from "@features/leasing/lease-view/lease-view.component";
import {LeaseGridSpace, LeaseSummary, OccupancyType, SharedLeasesSpaceSize} from "@features/leasing/leasing.model";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {AppSharedModule} from "@modules/app-shared.module";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {displayStatus} from "@ui-kit/framework/util/static/string-format-util";
import {BookingCalendarComponent} from '@ui-kit/webkit/components/booking-calendar/booking-calendar.component';
import {DateConfig} from '@ui-kit/webkit/components/booking-calendar/booking-calendar.model';
import {BookingConfig, SharedSpaceAvailability, Space, SpaceAvailability} from "../bookings.model";
import {LeaseBookingsService} from "../lease-bookings.service";
import {SpaceSelectionListComponent} from "../space-selection-list/space-selection-list.component";

// import {CalendarComponent as BookingCalendarComponent} from "./calendar/calendar.component";

@Component({
  selector: 'app-calendar-view',
  standalone: true,
  imports: [
    BookingCalendarComponent,
    FlexModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    CommonModule,
    MatDatepickerModule,
    FormsModule,
    AppSharedModule,
    SpaceSelectionListComponent,
    MatTab,
    MatTabLabel,
    MatTabGroup,
    MatBadgeModule,
    SharedSpaceSelectionListComponent,
    MatOption,
    MatSelect,
    // CalendarComponent
  ],
  templateUrl: './calendar-view.component.html',
  styleUrl: './calendar-view.component.scss'
})
export class CalendarViewComponent implements OnInit, OnChanges {
  @ViewChild("calendar") calendar!: BookingCalendarComponent;
  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() offering: any = {};

  @Input() spaces: Space[] = [];
  @Input() spaceAvailabilityMap: Map<string, Map<string, SpaceAvailability>> = new Map();
  @Input() loadingSpaceData: boolean = false;

  @Input() isSubLease: boolean = false;
  @Input() subLeases: LeaseSummary[] = [];
  @Input() sharedSpaceAvailabilityMap: Map<string, Map<string, SharedSpaceAvailability>> = new Map();

  @Output() focusMonthChanged = new EventEmitter<Date>();

  @Input() bookingConfig: BookingConfig = new BookingConfig();
  @Output() bookingConfigChange = new EventEmitter<BookingConfig>();

  @Input() availableSpaces: {
    fullyAvailable: Space[],
    partiallyAvailable: Space[]
  } = {fullyAvailable: [], partiallyAvailable: []};
  @Input() overlappingLeaseSpaces: LeaseGridSpace[] = [];

  @Input() availableSharedSpaces: {
    totalSpace: number,
    availableSpace: number
  } = {totalSpace: 0, availableSpace: 0};
  @Input() overlappingLeaseSharedSpaces: SharedLeasesSpaceSize[] = [];

  @Input() dateConfigs: Map<string, Map<string, DateConfig>> = new Map();

  @Input() allIndustries: SelectableCategory[] = [];

  @Output() updateAvailableSpaces = new EventEmitter<string[]>();
  @Output() updateOverlappingLeases = new EventEmitter<string[]>();
  @Output() createBookingEvent = new EventEmitter<void>();
  @Output() quickDateSelection = new EventEmitter<{
    dateRange: { start: string, end: string },
    dateSelection: string[]
  }>();

  preSelectedDates: string[] = [];
  selectedSpaces: string[] = [];
  reservedSpace: number = 0;

  selectedTabCode: 'available' | 'overlapping' = 'available';
  selectedTabIndex: number = 0;

  searchDatesControl: { start: Date | null, end: Date | null } = {start: null, end: null};
  initGrid: boolean = false;

  constructor(public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private bookingsService: LeaseBookingsService,
              private tabService: TabManagementService) {
  }

  ngOnInit() {
    this.initGrid = true;
    if (this.bookingConfig) {
      this.preSelectedDates = this.bookingConfig.preSelectedDates;
      this.selectedSpaces = this.bookingConfig.spaceIds;
      this.reservedSpace = this.bookingConfig.subLeaseSize;
      this.emitSpaceUpdates();
      this.emitBookingDatesChange();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['bookingConfig'] && this.bookingConfig.preSelectedDates) {
      this.initGrid = true;
      this.preSelectedDates = [...this.bookingConfig.preSelectedDates];
      this.selectedSpaces = [...this.bookingConfig.spaceIds];
      this.reservedSpace = this.bookingConfig.subLeaseSize;
      this.emitSpaceUpdates();
      this.emitBookingDatesChange();
      this.onDateSelectionChange(this.preSelectedDates);
    }

    if (!this.isSubLease && changes['availableSpaces'] && this.availableSpaces) {
      this.checkSelectedSpaces();
    }
  }

  checkSelectedSpaces() {
    const selectableSpaces = this.availableSpaces.fullyAvailable.concat(this.availableSpaces.partiallyAvailable);
    const availableSelectedSpaces = this.bookingConfig.spaceIds.filter(f => selectableSpaces.some(m => m.id == f));
    this.selectedSpaces = [...availableSelectedSpaces];
    this.emitSpaceIdsChange();
  }

  onDateSelectionChange(dates: string[]) {
    this.preSelectedDates = dates;
    // don't update pattern type automatically if editing the lease, as it is already set
    if (this.editType != 'edit') {
      this.bookingConfig.patternType = this.bookingsService.getDefaultPatternType(this.preSelectedDates);
    }
    this.emitSpaceUpdates();
    this.emitBookingDatesChange();
  }

  onBookSelected() {
    this.emitBookingDatesChange();
    this.emitSpaceIdsChange();
    this.createBookingEvent.emit();
  }

  onSelectDateRange() {
    if (this.searchDatesControl.start && this.searchDatesControl.end) {
      const searchDates = {
        start: this.dateUtils.displayShortDate(this.searchDatesControl.start)!,
        end: this.dateUtils.displayShortDate(this.searchDatesControl.end)!
      }

      this.preSelectedDates = this.bookingsService.generateDateRange(searchDates.start, searchDates.end);
      this.quickDateSelection.emit({dateRange: searchDates, dateSelection: this.preSelectedDates});

      this.calendar.setSelectedDates(this.preSelectedDates);
      this.emitBookingDatesChange();
    }
  }

  emitSpaceUpdates() {
    this.updateAvailableSpaces.emit(this.preSelectedDates);
    this.updateOverlappingLeases.emit(this.preSelectedDates);
  }

  onSelectSpace(spaceId: string) {
    this.selectedSpaces = this.bookingsService.onSelectSpace(spaceId, this.selectedSpaces, this.bookingConfig);
    this.emitSpaceIdsChange();
  }

  onSharedSpaceSizeChange(spaceSize: number) {
    this.reservedSpace = spaceSize;
    this.emitSpaceIdsChange();
  }

  emitBookingDatesChange() {
    this.bookingConfig.preSelectedDates = this.preSelectedDates.sort();
    this.bookingConfig.startDate = this.preSelectedDates[0];
    this.bookingConfig.endDate = this.preSelectedDates[this.preSelectedDates.length - 1];
    this.bookingConfigChange.emit(this.bookingConfig);
  }

  emitSpaceIdsChange() {
    this.bookingConfig.spaceIds = this.selectedSpaces;
    this.bookingConfig.subLeaseSize = this.reservedSpace;
    this.bookingConfigChange.emit(this.bookingConfig);
  }

  switchTabs($event: MatTabChangeEvent): void {
    if ($event.index === 0) {
      this.selectedTabCode = 'available'
    } else {
      this.selectedTabCode = 'overlapping';
    }
  }

  get disableConfirm() {
    if (this.isSubLease) {
      return !this.reservedSpace || this.reservedSpace <= 0 || this.reservedSpace > this.availableSharedSpaces.totalSpace;
    }
    return this.selectedSpaces.length === 0;
  }

  get filterOverlappingLeaseSpaces() {
    if (this.selectedSpaces && this.selectedSpaces.length > 0) {
      return this.overlappingLeaseSpaces.filter(f => this.selectedSpaces
        .map(m => m.toString())
        .includes(f.districtSpaceId.toString()));
    }
    return this.overlappingLeaseSpaces;
  }

  subLease(subLeaseUuid: string) {
    return this.subLeases.find(f => f.uuid == subLeaseUuid);
  }

  openLeaseViewTab(lgs: LeaseGridSpace) {
    const payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(lgs.leaseUuid!),
      pageHeader: this.offering.label,
      data: {id: lgs.leaseUuid, occupancyType: lgs.occupancyType, agreementType: lgs.agreementType},
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  openSubLeaseViewTab(lease?: LeaseSummary) {
    if (!lease) return;
    const payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(lease.uuid!),
      pageHeader: this.offering.label,
      data: {id: lease.uuid, occupancyType: lease.occupancyType, agreementType: lease.agreementType},
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  protected readonly PanelType = PanelType;
  protected readonly displayStatus = displayStatus;
  protected readonly OccupancyType = OccupancyType;
}
